import React from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { Divider, Empty, Image, Space, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { WarehouseDocumentFileSchema } from '@app/models/wms';

import { Container, FileIcon, IconContainer, IconLink, Item, List, PreviewContainer } from './styles';

interface IProps {
  files: WarehouseDocumentFileSchema[];
}

const { Link } = Typography;

const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const downloadFileByUrl = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    triggerDownload(blobUrl, fileName);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  } catch {
    triggerDownload(url, fileName);
  }
};

export const FileList: React.FC<IProps> = ({ files }) => {
  const documentsT = useTranslation(NSKeys.warehouseDocuments);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewCurrent, setPreviewCurrent] = React.useState(0);
  const downloadableFiles = files.filter((file) => file.variants.file?.url || file.variants.thumbnail?.url);
  const previewFiles = downloadableFiles.filter((file) => file.variants.thumbnail?.url);

  const handlePreviewDownload = (index?: number) => {
    const file = index !== undefined ? previewFiles[index] : undefined;
    const downloadUrl = file?.variants.file?.url || file?.variants.fullscreen?.url || file?.variants.thumbnail?.url;

    if (file && downloadUrl) {
      void downloadFileByUrl(downloadUrl, file.name);
    }
  };

  const handlePreviewOpen = (index: number) => {
    setPreviewCurrent(index);
    setPreviewOpen(true);
  };

  if (downloadableFiles.length === 0) {
    return (
      <Container>
        <Empty description={documentsT.t('files.empty')} />
      </Container>
    );
  }

  return (
    <>
      <Divider titlePlacement="left">{documentsT.t('section.files')}</Divider>
      <Container>
        <Image.PreviewGroup
          preview={{
            open: previewOpen,
            current: previewCurrent,
            rootClassName: 'previewGroupRootClassName',
            onChange: (current) => setPreviewCurrent(current),
            onOpenChange: (open, { current }) => {
              setPreviewOpen(open);
              setPreviewCurrent(current);
            },
            actionsRender: (originalNode, { current }) => (
              <Space size={12} className="toolbar-wrapper">
                {originalNode}
                <Tooltip title={documentsT.t('files.downloadHint')}>
                  <DownloadOutlined onClick={() => handlePreviewDownload(current)} />
                </Tooltip>
              </Space>
            ),
          }}>
          <List>
            {downloadableFiles.map((file) => {
              const fileUrl = file.variants.file?.url;
              const thumbnailUrl = file.variants.thumbnail?.url;
              const fullscreenUrl = file.variants.fullscreen?.url;
              const previewIndex = previewFiles.indexOf(file);
              const preview = thumbnailUrl ? (
                <PreviewContainer>
                  <Image
                    alt={file.name}
                    height={40}
                    preview={{ src: fullscreenUrl || thumbnailUrl }}
                    src={thumbnailUrl}
                    width={40}
                  />
                </PreviewContainer>
              ) : (
                <IconLink href={fileUrl} target="_blank" rel="noreferrer">
                  <IconContainer>
                    <FileIcon />
                  </IconContainer>
                </IconLink>
              );

              return (
                <Item key={`${file.name}-${fileUrl || thumbnailUrl}`}>
                  {preview}
                  {thumbnailUrl ? (
                    <Link onClick={() => handlePreviewOpen(previewIndex)}>{file.name}</Link>
                  ) : fileUrl ? (
                    <Link href={fileUrl} target="_blank" rel="noreferrer">
                      {file.name}
                    </Link>
                  ) : (
                    file.name
                  )}
                </Item>
              );
            })}
          </List>
        </Image.PreviewGroup>
      </Container>
    </>
  );
};
